import { FormEvent, useEffect, useState } from "react";
import { Edit, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminUser, PaginationMeta, getCurrentAdmin, logout } from "./api";
import { NewsCategoryOption, NewsItem, deleteAdminNewsItem, listAdminNewsItems } from "@/services/news";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

export default function AdminNewsItems() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<NewsItem[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<NewsCategoryOption[]>([]);
  const [counts, setCounts] = useState({ Published: 0, Hidden: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, NewsCategoryId: categoryId, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminNewsItems(filters);
      setItems(data.Items);
      setCounts(data.Counts || { Published: 0, Hidden: 0 });
      setCategoryOptions(data.Categories || []);
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "最新消息資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", NewsCategoryId: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, NewsCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  async function handleDelete(item: NewsItem) {
    const confirmed = await confirm({
      title: "刪除最新消息？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.title}</strong> 嗎？
          <br />
          刪除後前台也會立即移除，且無法復原。
        </>
      ),
      confirmText: "刪除內容",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminNewsItem(item.id);
      await load({ Status: status, NewsCategoryId: categoryId, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "最新消息刪除失敗。");
    }
  }

  const allItemCount = counts.Published + counts.Hidden || pagination.Total;

  return (
    <AdminLayout sectionLabel="內容管理" title="最新消息管理" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1 md:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { value: "", label: "全部消息", count: allItemCount, tone: "text-[#315746]" },
            { value: "Published", label: "上架中", count: counts.Published, tone: "text-[#245438]" },
            { value: "Hidden", label: "下架", count: counts.Hidden, tone: "text-[#59665f]" },
          ].map((option) => (
            <button
              className={`flex min-w-[96px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${status === option.value ? "border-[#315746] bg-[#315746] text-white" : `border-[#dfe6e1] bg-white ${option.tone}`}`}
              key={option.value}
              onClick={() => {
                setStatus(option.value);
                load({ Status: option.value, NewsCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
              }}
              type="button"
            >
              <span className="text-xs font-bold">{option.label}</span>
              <span className="ml-3 text-lg font-bold">{option.count}</span>
            </button>
          ))}
        </div>

        <div className="hidden gap-3 md:grid md:flex-1 md:grid-cols-3">
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "", NewsCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">全部消息</p>
            <p className="mt-1 text-2xl font-bold text-[#203d32]">{allItemCount}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Published", NewsCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">上架中</p>
            <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Published}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Hidden", NewsCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">下架</p>
            <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Hidden}</p>
          </button>
        </div>
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="news-item-status">狀態</label>
          <select id="news-item-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Published">上架</option>
            <option value="Hidden">下架</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[160px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="news-item-category">分類</label>
          <select id="news-item-category" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            <option value="">全部分類</option>
            {categoryOptions.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="news-item-keyword">關鍵字</label>
          <input id="news-item-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="標題或內容" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, NewsCategoryId: categoryId, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={() => navigate("/admin/news-items/new")} type="button">
          <Plus size={20} />
          新增消息
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[960px] table-fixed border-separate border-spacing-0 text-left">
            <thead className="sticky top-0 z-40 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[420px] px-4 py-3">主題</th>
                <th className="w-[130px] px-4 py-3">分類</th>
                <th className="w-[130px] px-4 py-3">發布日</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[80px] px-4 py-3">排序</th>
                <th className="sticky right-0 z-50 w-[160px] bg-[#f0f3f1] px-4 py-3 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>沒有符合條件的最新消息。</td></tr>
              ) : items.map((item) => {
                const categoryName = categoryOptions.find((option) => option.id === item.newsCategoryId)?.name || "-";
                return (
                  <tr className="border-t border-[#edf0ee]" key={item.id}>
                    <td className="px-4 py-4">
                      <strong className="block truncate text-[#203d32]">{item.title}</strong>
                    </td>
                    <td className="px-4 py-4 text-sm font-bold text-[#315746]">{categoryName}</td>
                    <td className="px-4 py-4 text-sm">{item.date || "-"}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.isPublished ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.isPublished ? "上架" : "下架"}</span>
                    </td>
                    <td className="px-4 py-4">{item.sortOrder}</td>
                    <td className="sticky right-0 z-10 bg-white px-4 py-4 shadow-[-10px_0_18px_rgba(32,61,50,.08)]">
                      <div className="grid gap-2">
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/news-items/${item.id}/edit`)} type="button">
                          <Edit size={16} />
                          編輯
                        </button>
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                          <Trash2 size={16} />
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, NewsCategoryId: categoryId, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      <ConfirmDialog />
    </AdminLayout>
  );
}
